/**
 * Slack's conversation shortcuts (core logic/shortcuts): ⌥↑ / ⌥↓ previous / next conversation in sidebar order,
 * ⌥⇧↑ / ⌥⇧↓ previous / next unread, Esc marks the conversation on screen read, ⌘⇧A All unreads, ⌘/ the sheet,
 * ⌘N New message.
 */
import { Shell, Shortcuts } from "@bundle/core";
import { useSidebar, useWorkspace } from "@bundle/ui";
import { useEffect, useRef } from "react";

/** Something modal is open (a dialog, sheet or menu): Esc belongs to it. */
function overlayOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]') !== null;
}

export function useSlackShortcuts(p: { activeUrl: string; open: (url: string) => void; onHelp: () => void; onNewMessage?: () => void }): void {
  const ws = useWorkspace();
  const sidebar = useSidebar();
  // The latest values without re-binding the listener on every render.
  const latest = useRef({ ...p, ws, sidebar });
  useEffect(() => {
    latest.current = { ...p, ws, sidebar };
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const { activeUrl, open, onHelp, onNewMessage, ws, sidebar } = latest.current;
      const mod = e.metaKey || e.ctrlKey;
      const route = Shell.parseUrl(activeUrl);
      if (e.altKey && !mod && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        const order = Shortcuts.conversationOrder(sidebar.state.sections, sidebar.state.collapsed);
        const next = Shortcuts.adjacentConversation(order, route, e.key === "ArrowUp" ? -1 : 1, e.shiftKey);
        e.preventDefault();
        if (next !== undefined) open(Shell.formatUrl(Shortcuts.stopRoute(next, ws.slug)));
      } else if (mod && e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        open(Shell.formatUrl({ type: "unreads", ws: ws.slug }));
      } else if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "n" && onNewMessage !== undefined) {
        // ⌘N: New message (Slack).
        e.preventDefault();
        onNewMessage();
      } else if (mod && (e.key === "/" || e.code === "Slash")) {
        e.preventDefault();
        onHelp();
      } else if (e.key === "Escape" && !e.defaultPrevented && !mod && !e.shiftKey && !overlayOpen()) {
        if (route?.type !== "channel") return;
        const c = ws.state.cache?.channels[route.channelId];
        if (c !== undefined) ws.vm.deps.data.markRead(ws.vm.wsId, c.id, c.lastMessageSeq);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
